/**
 * Popover curto do mapa: o que é o ambiente, a peça desmembrada, a pessoa ou o
 * "+N" que foi tocado, e o caminho para a página do setor. Fica dentro do
 * palco, ancorado no elemento tocado (abaixo dele, ou acima se não couber).
 *
 * Fecha com Esc, com toque fora ou quando a vista se mexe (o index cuida).
 * Ao abrir, o foco entra no popover; ao fechar, volta para onde estava.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiArrowRight, FiX } from "react-icons/fi";

import { formatarDataLocal, plural } from "../../utils/dinheiro";
import { ORIGEM_PECA } from "./dados/pecas";
import {
  ROTULO_PRIORIDADE,
  ROTULO_STATUS_AMBIENTE,
  ROTULO_STATUS_PECA,
  ROTULO_STATUS_TAREFA,
  TOM_PRIORIDADE,
  TOM_STATUS_AMBIENTE,
  TOM_STATUS_TAREFA,
} from "./dados/vocabulario";
import { ROTULO_SITUACAO_PESSOA, TOM_SITUACAO_PESSOA } from "./pessoas";
import { doSetor } from "./textos";
import { AcoesPopover, CaixaPopover, ListaPopover, Selo, Selos } from "./styles";

const LARGURA = 320;
const MARGEM = 8;
const ALTURA_MINIMA_ABAIXO = 220;
const MAXIMO_TAREFAS = 3;
const MAXIMO_OCULTAS = 8;
const MAXIMO_PECAS = 6;

function posicionar(ancora, limites, altura) {
  const largura = Math.min(LARGURA, Math.max(0, limites.largura - 2 * MARGEM));
  const centro = ancora.x + ancora.largura / 2;
  const esquerda = Math.min(Math.max(MARGEM, centro - largura / 2), Math.max(MARGEM, limites.largura - largura - MARGEM));
  const abaixo = ancora.y + ancora.altura + MARGEM;
  const cabeAbaixo = abaixo + Math.max(altura, ALTURA_MINIMA_ABAIXO) <= limites.altura - MARGEM;
  const cabeAcima = ancora.y - MARGEM - altura >= MARGEM;
  if (cabeAbaixo || !cabeAcima) {
    return { left: esquerda, top: Math.min(abaixo, Math.max(MARGEM, limites.altura - altura - MARGEM)), width: largura, origem: "top" };
  }
  return { left: esquerda, top: ancora.y - MARGEM - altura, width: largura, origem: "bottom" };
}

const SEM_LIMITES = { largura: Number.POSITIVE_INFINITY, altura: Number.POSITIVE_INFINITY };
const SEM_MEDIDA = { altura: 0, largura: SEM_LIMITES.largura, alturaPalco: SEM_LIMITES.altura };

/** Caixa posicionada + comportamento (Esc, toque fora, foco). */
export default function PopoverMapa({ ancora, titulo, onFechar, children }) {
  const caixaRef = useRef(null);
  const [medida, setMedida] = useState(SEM_MEDIDA);

  // Mede antes de pintar (a caixa aparece já no lugar certo) e de novo
  // quando o conteúdo muda de altura (o polling atualiza o que está aberto).
  // Só grava se a medida mudou.
  const medir = useCallback(() => {
    const caixa = caixaRef.current;
    const palco = caixa?.offsetParent;
    const nova = {
      altura: caixa?.offsetHeight || 0,
      largura: palco?.clientWidth || SEM_LIMITES.largura,
      alturaPalco: palco?.clientHeight || SEM_LIMITES.altura,
    };
    setMedida((atual) =>
      atual.altura === nova.altura && atual.largura === nova.largura && atual.alturaPalco === nova.alturaPalco
        ? atual
        : nova
    );
  }, []);

  useLayoutEffect(() => {
    medir();
  }, [medir, ancora, titulo]);

  useEffect(() => {
    const caixa = caixaRef.current;
    if (!caixa || typeof ResizeObserver === "undefined") return undefined;
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [medir]);

  useEffect(() => {
    const anterior = document.activeElement;
    caixaRef.current?.focus({ preventScroll: true });
    const aoTeclar = (e) => {
      if (e.key === "Escape") onFechar();
    };
    const aoTocarFora = (e) => {
      if (!caixaRef.current?.contains(e.target)) onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    document.addEventListener("pointerdown", aoTocarFora, true);
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.removeEventListener("pointerdown", aoTocarFora, true);
      if (anterior && typeof anterior.focus === "function" && document.contains(anterior)) {
        anterior.focus({ preventScroll: true });
      }
    };
  }, [onFechar]);

  const limites = { largura: medida.largura, altura: medida.alturaPalco };
  const { left, top, width, origem } = posicionar(ancora, limites, medida.altura);
  return (
    <CaixaPopover
      ref={caixaRef}
      role="dialog"
      aria-label={titulo}
      tabIndex={-1}
      data-origem={origem}
      style={{ left, top, width }}
    >
      <button type="button" className="fechar" aria-label="Fechar" onClick={onFechar}>
        <FiX aria-hidden="true" />
      </button>
      {children}
    </CaixaPopover>
  );
}

// ---------------------------------------------------------- conteúdos

function Linha({ rotulo, children }) {
  if (children == null || children === "") return null;
  return (
    <>
      <dt>{rotulo}</dt>
      <dd>{children}</dd>
    </>
  );
}

function lugarDentroDoSetor(item, indice) {
  const subsetor = indice.subsetores.get(item.subsetorId)?.nome;
  const posto = indice.postos.get(item.postoId)?.nome;
  if (!subsetor && !posto) return `Fila ${doSetor(item.setorNome)}`;
  return [item.setorNome, subsetor, posto].filter(Boolean).join(" › ");
}

function textoDoPrazoDoAmbiente(demanda) {
  if (!demanda.prazoEfetivo) return "Sem prazo";
  const dia = formatarDataLocal(demanda.prazoEfetivo);
  if (demanda.origemPrazo !== "app") return `${dia} (planilha)`;
  const planilha = demanda.prazoPlanilha ? `, planilha ${formatarDataLocal(demanda.prazoPlanilha)}` : "";
  return `${dia} (definido no app${planilha})`;
}

// Rótulo tem que dizer a régua (ADR-0059): prazo herdado do ambiente não pode
// se passar por prazo que alguém decidiu para a peça.
const REGUA_DO_PRAZO = {
  [ORIGEM_PECA.PECA]: "definido na peça",
  [ORIGEM_PECA.AMBIENTE]: "herdado do ambiente",
};

function textoDoPrazoDaPeca(peca) {
  if (!peca.prazoEfetivo) return "Sem prazo";
  const dia = formatarDataLocal(peca.prazoEfetivo);
  const regua = REGUA_DO_PRAZO[peca.origemPrazo];
  return regua ? `${dia} (${regua})` : dia;
}

function textoDoMarceneiro(marceneiro) {
  if (!marceneiro || marceneiro.semMarceneiro) return null;
  return marceneiro.tipo ? `${marceneiro.nome} (${marceneiro.tipo})` : marceneiro.nome;
}

function TarefasResumidas({ tarefas }) {
  if (!tarefas?.length) return null;
  const visiveis = tarefas.slice(0, MAXIMO_TAREFAS);
  return (
    <ListaPopover aria-label="Tarefas abertas">
      {visiveis.map((t) => (
        <li key={t.id}>
          <Selo $tom={TOM_STATUS_TAREFA[t.status]}>{ROTULO_STATUS_TAREFA[t.status]}</Selo>
          <span className="texto">{[t.codigo, t.titulo].filter(Boolean).join(" · ")}</span>
        </li>
      ))}
      {tarefas.length > MAXIMO_TAREFAS && <li className="mais">e mais {tarefas.length - MAXIMO_TAREFAS}</li>}
    </ListaPopover>
  );
}

function BotaoSetor({ setorId, nome, onAbrirSetor }) {
  if (!setorId) return null;
  return (
    <button type="button" className="principal" onClick={() => onAbrirSetor(setorId)}>
      Abrir {nome || "o setor"}
      <FiArrowRight aria-hidden="true" />
    </button>
  );
}

/** Lista das peças que saíram do ambiente, com o lugar de cada uma. */
function PecasDoAmbiente({ pecas, onEscolherPeca }) {
  if (!pecas?.length) return null;
  const visiveis = pecas.slice(0, MAXIMO_PECAS);
  return (
    <ListaPopover aria-label="Peças fora do ambiente">
      {visiveis.map((peca) => (
        <li key={peca.id}>
          <button type="button" onClick={() => onEscolherPeca?.(peca)}>
            <Selo $tom={peca.tomStatus}>{peca.rotuloStatus}</Selo>
            <span className="texto">{peca.nome || peca.codigo}</span>
            <span className="mais">{peca.setorNome || "sem setor"}</span>
          </button>
        </li>
      ))}
      {pecas.length > MAXIMO_PECAS && <li className="mais">e mais {pecas.length - MAXIMO_PECAS}</li>}
    </ListaPopover>
  );
}

/** O que é o AMBIENTE tocado (a ficha grande do mapa). */
export function ConteudoAmbiente({ demanda, pecasFora = [], indice, onAbrirSetor, onDetalhes, onEscolherPeca }) {
  const subtitulo = [demanda.cliente, demanda.lote && `Lote ${demanda.lote}`].filter(Boolean).join(" · ");
  const marceneiro = textoDoMarceneiro(demanda.marceneiro);
  return (
    <>
      <h3>{demanda.rotulo || "Ambiente sem identificação"}</h3>
      {subtitulo && <p className="subtitulo">{subtitulo}</p>}
      <Selos>
        <Selo $tom={TOM_STATUS_AMBIENTE[demanda.statusApp]}>{ROTULO_STATUS_AMBIENTE[demanda.statusApp]}</Selo>
        {demanda.atrasada && <Selo $tom="danger">Atrasado</Selo>}
        {demanda.movidoAMao && <Selo $tom="neutral">Movido à mão</Selo>}
        {demanda.servico && <Selo $tom="info">Serviço</Selo>}
        {demanda.aguardandoEtapa && <Selo $tom="warning">Aguardando {demanda.aguardandoEtapa}</Selo>}
        {(demanda.pendencias || []).map((p) => <Selo key={p.codigo} $tom="warning">{p.texto}</Selo>)}
      </Selos>
      <dl>
        <Linha rotulo="Onde">{lugarDentroDoSetor(demanda, indice)}</Linha>
        <Linha rotulo="Etapa da planilha">{demanda.etapaRotulo || null}</Linha>
        <Linha rotulo="Pela planilha">
          {demanda.movidoAMao ? demanda.setorNomePlanilha || "Sem sala no mapa" : null}
        </Linha>
        <Linha rotulo="Prazo">{textoDoPrazoDoAmbiente(demanda)}</Linha>
        <Linha rotulo="Previsto">{demanda.prevista ? formatarDataLocal(demanda.prevista) : null}</Linha>
        <Linha rotulo="No setor há">{demanda.diasNoSetor != null ? plural(demanda.diasNoSetor, "dia", "dias") : null}</Linha>
        <Linha rotulo="Marceneiro">{marceneiro}</Linha>
        <Linha rotulo="Motivo da pausa">{demanda.statusApp === "pausado" ? demanda.motivoPausa || null : null}</Linha>
      </dl>
      {demanda.pecasFora > 0 && (
        <p className="subtitulo">{`${plural(demanda.pecasFora, "peça", "peças")} fora deste ambiente`}</p>
      )}
      <PecasDoAmbiente pecas={pecasFora} onEscolherPeca={onEscolherPeca} />
      <TarefasResumidas tarefas={demanda.tarefas} />
      <AcoesPopover>
        <BotaoSetor setorId={demanda.setorId} nome={demanda.setorNome} onAbrirSetor={onAbrirSetor} />
        <button type="button" onClick={() => onDetalhes(demanda)}>Detalhes do ambiente</button>
      </AcoesPopover>
    </>
  );
}

/** O que é a PEÇA DESMEMBRADA tocada (a pecinha menor do mapa). */
export function ConteudoPeca({ peca, indice, onAbrirSetor, onAbrirAmbiente }) {
  const subtitulo = [peca.codigo, peca.ambienteRotulo].filter(Boolean).join(" · ");
  return (
    <>
      <h3>{peca.nome || peca.codigo || "Peça sem nome"}</h3>
      {subtitulo && <p className="subtitulo">{subtitulo}</p>}
      <Selos>
        <Selo $tom={peca.tomStatus}>{peca.rotuloStatus || ROTULO_STATUS_PECA[peca.status]}</Selo>
        {peca.atrasada && <Selo $tom="danger">Atrasada</Selo>}
        {peca.prioridade !== "normal" && (
          <Selo $tom={TOM_PRIORIDADE[peca.prioridade]}>{ROTULO_PRIORIDADE[peca.prioridade]}</Selo>
        )}
      </Selos>
      <dl>
        <Linha rotulo="Onde">{lugarDentroDoSetor(peca, indice)}</Linha>
        <Linha rotulo="Saiu de">{peca.ambienteRotulo || null}</Linha>
        <Linha rotulo="Cliente">{peca.cliente || null}</Linha>
        <Linha rotulo="Prazo">{textoDoPrazoDaPeca(peca)}</Linha>
        <Linha rotulo="Previsto">{peca.prevista ? formatarDataLocal(peca.prevista) : null}</Linha>
        <Linha rotulo="Responsável">{indice.colaboradores.get(peca.colaboradorId)?.nome || null}</Linha>
      </dl>
      <TarefasResumidas tarefas={peca.tarefas} />
      <AcoesPopover>
        <BotaoSetor setorId={peca.setorId} nome={peca.setorNome} onAbrirSetor={onAbrirSetor} />
        {peca.ambiente && (
          <button type="button" onClick={() => onAbrirAmbiente?.(peca.ambiente)}>Ver o ambiente</button>
        )}
      </AcoesPopover>
    </>
  );
}

/** O "+N": os itens que não couberam naquele lugar da sala. */
export function ConteudoMais({ mais, nomeDoSetor, onEscolher, onEscolherPeca, onAbrirSetor }) {
  const ambientes = mais.ocultas.slice(0, MAXIMO_OCULTAS);
  const pecas = (mais.ocultasPecas || []).slice(0, MAXIMO_PECAS);
  const total = mais.ocultas.length + (mais.ocultasPecas?.length || 0);
  const restantes = total - ambientes.length - pecas.length;
  return (
    <>
      <h3>{`Mais ${plural(total, "item", "itens")}`}</h3>
      <p className="subtitulo">
        {mais.conteiner === "fila" ? `Fila ${doSetor(nomeDoSetor)}` : `Neste lugar ${doSetor(nomeDoSetor)}`}
      </p>
      {ambientes.length > 0 && (
        <ListaPopover aria-label="Ambientes agrupados">
          {ambientes.map((d) => (
            <li key={d.chaveLinha}>
              <button type="button" onClick={() => onEscolher(d)}>
                <Selo $tom={d.atrasada ? "danger" : TOM_STATUS_AMBIENTE[d.statusApp]}>
                  {d.atrasada ? "Atrasado" : ROTULO_STATUS_AMBIENTE[d.statusApp]}
                </Selo>
                <span className="texto">{d.rotulo || d.ambiente}</span>
              </button>
            </li>
          ))}
        </ListaPopover>
      )}
      {pecas.length > 0 && (
        <ListaPopover aria-label="Peças agrupadas">
          {pecas.map((peca) => (
            <li key={peca.id}>
              <button type="button" onClick={() => onEscolherPeca?.(peca)}>
                <Selo $tom={peca.atrasada ? "danger" : peca.tomStatus}>
                  {peca.atrasada ? "Atrasada" : peca.rotuloStatus}
                </Selo>
                <span className="texto">{peca.nome || peca.codigo}</span>
              </button>
            </li>
          ))}
        </ListaPopover>
      )}
      {restantes > 0 && <p className="subtitulo">e mais {restantes}</p>}
      <AcoesPopover>
        <BotaoSetor setorId={mais.setorId} nome={nomeDoSetor} onAbrirSetor={onAbrirSetor} />
      </AcoesPopover>
    </>
  );
}

export function ConteudoPessoa({ situacao, setorId, nomeDoSetor, indice, onAbrirSetor }) {
  const { colaborador } = situacao;
  const postos = situacao.postoIds.map((id) => indice.postos.get(id)?.nome).filter(Boolean);
  return (
    <>
      <h3>{colaborador.nome}</h3>
      <Selos>
        <Selo $tom={TOM_SITUACAO_PESSOA[situacao.situacao]}>{ROTULO_SITUACAO_PESSOA[situacao.situacao]}</Selo>
      </Selos>
      <dl>
        <Linha rotulo="Agora">{situacao.atividade?.texto}</Linha>
        <Linha rotulo={postos.length === 1 ? "Posto" : "Postos"}>{postos.length ? postos.join(", ") : "Sem posto"}</Linha>
        <Linha rotulo="Na planilha">{colaborador.apelido_planilha || null}</Linha>
        <Linha rotulo="Ambientes">{situacao.ambientes.length ? plural(situacao.ambientes.length, "ambiente", "ambientes") : null}</Linha>
      </dl>
      <TarefasResumidas tarefas={situacao.tarefasAbertas} />
      <AcoesPopover>
        <BotaoSetor setorId={setorId} nome={nomeDoSetor} onAbrirSetor={onAbrirSetor} />
      </AcoesPopover>
    </>
  );
}
